import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const PREDEFINED_REASONS = [
  'Customer declined',
  'Budget issues',
  'Competitor signed',
  'Not reachable',
  'Wrong timing',
  'Duplicate entry',
  'Incorrect data',
  'Customer relocated',
  'Already member elsewhere'
];

const NotInterestedDialog = ({ open, onOpenChange, commitment, onSuccess }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOther = selectedReason === 'Other';
  const canSubmit = selectedReason && (!isOther || otherReason.trim().length >= 10);

  const handleSubmit = async () => {
    if (!canSubmit || !commitment) return;

    setIsSubmitting(true);
    try {
      const reason = isOther ? `Other: ${otherReason.trim()}` : selectedReason;
      await axiosInstance.post(`/commitments/${commitment.id}/not-interested`, { reason });
      toast.success('Commitment marked as Not Interested (pending manager approval)');
      // Reset state
      setSelectedReason('');
      setOtherReason('');
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as Not Interested');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (openState) => {
    if (!openState) {
      setSelectedReason('');
      setOtherReason('');
    }
    onOpenChange(openState);
  };

  if (!commitment) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Mark as Not Interested
          </DialogTitle>
        </DialogHeader>

        {/* Commitment context */}
        <div className="p-3 bg-gray-50 rounded-md text-sm space-y-1">
          <div><span className="text-gray-500">Gym:</span> <span className="font-medium">{commitment.gym_name}</span></div>
          <div><span className="text-gray-500">Amount:</span> <span className="font-medium">₹{parseFloat(commitment.revenue_expected || 0).toLocaleString('en-IN')}</span></div>
          <div><span className="text-gray-500">Date:</span> <span className="font-medium">{commitment.entry_date || commitment.current_date}</span></div>
        </div>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Reason <span className="text-red-500">*</span></Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {PREDEFINED_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
                <SelectItem value="Other">Other (specify)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isOther && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Specify Reason <span className="text-red-500">*</span></Label>
                <span className={`text-xs ${otherReason.trim().length >= 10 ? 'text-green-600' : 'text-gray-500'}`}>
                  {otherReason.trim().length}/10 min
                </span>
              </div>
              <Input
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Enter reason (minimum 10 characters)"
                className={otherReason.trim().length > 0 && otherReason.trim().length < 10 ? 'border-red-300' : ''}
              />
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500">
          This commitment will require manager approval to finalize the Not Interested status.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            variant="destructive"
          >
            {isSubmitting ? 'Submitting...' : 'Mark Not Interested'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotInterestedDialog;
